import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, ArrowDownCircle, ArrowUpCircle, Filter, X, CalendarPlus, DollarSign, Wallet, Lock, Unlock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";

const PM_LABELS = {
  dinheiro: "Dinheiro",
  pix: "PIX",
  credito: "Crédito",
  debito: "Débito",
  transferencia: "Transferência",
  boleto: "Boleto",
  fiado: "Fiado",
  credito_cliente: "Crédito do cliente",
};

const PM_COLORS = {
  dinheiro: "#16A34A",
  pix: "#3B82F6",
  credito: "#8B5CF6",
  debito: "#F59E0B",
  transferencia: "#06B6D4",
  boleto: "#64748B",
  fiado: "#EF4444",
  credito_cliente: "#EC4899",
  outros: "#94A3B8",
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const DEFAULT_FILTERS = () => ({
  from: todayISO(),
  to: todayISO(),
  type: "all",
  payment_method: "all",
  category: "all",
  unit_id: "all",
});

const currency = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const dtFmt = (d) => new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Finance() {
  const { apiCall, user } = useAuth();
  const isMaster = user?.role === "master";
  const [transactions, setTransactions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [units, setUnits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cashSession, setCashSession] = useState(null);
  const [expectedCash, setExpectedCash] = useState(0);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [form, setForm] = useState({ type: "receita", category: "", amount: "", payment_method: "dinheiro", description: "", date: "", due_date: "", paid_at: "", bank: "", invoice_number: "", status: "pago" });

  // Cash session dialog
  const [cashDialogOpen, setCashDialogOpen] = useState(false);
  const [cashOpeningAmount, setCashOpeningAmount] = useState("");
  const [cashClosingDeclared, setCashClosingDeclared] = useState("");
  const [cashNotes, setCashNotes] = useState("");
  const [cashMode, setCashMode] = useState("open");  // "open" | "close" | "movement"
  // iter67: sangria/reforço
  const [mvKind, setMvKind] = useState("withdrawal");
  const [mvAmount, setMvAmount] = useState("");
  const [mvReason, setMvReason] = useState("");
  // iter67: summary detalhado para o modal de fechamento
  const [closeSummary, setCloseSummary] = useState(null);

  const apptMap = useMemo(() => {
    const m = {};
    for (const a of appointments) {
      m[a.appointment_id] = {
        client_name: a.client_name,
        service_names: (a.services || []).map((s) => s.service_name || s.name).filter(Boolean).join(", "),
        date: a.date,
        status: a.status,
      };
    }
    return m;
  }, [appointments]);

  const loadSummary = useCallback(async (f) => {
    const params = new URLSearchParams();
    if (f.from) params.append("date_from", f.from);
    if (f.to) params.append("date_to", f.to);
    if (f.unit_id && f.unit_id !== "all") params.append("unit_id", f.unit_id);
    if (f.payment_method && f.payment_method !== "all") params.append("payment_method", f.payment_method);
    try {
      const r = await apiCall("get", `/finance/day-summary?${params.toString()}`);
      setSummary(r.data);
    } catch (e) {
      console.error("summary error", e);
    }
  }, [apiCall]);

  const loadCash = useCallback(async () => {
    try {
      const r = await apiCall("get", "/cash-sessions/current");
      setCashSession(r.data?.session || null);
      setExpectedCash(r.data?.expected_cash || 0);
    } catch (e) {
      console.error("cash error", e);
    }
  }, [apiCall]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, aRes, uRes] = await Promise.all([
        apiCall("get", "/transactions"),
        apiCall("get", "/appointments"),
        apiCall("get", "/units").catch(() => ({ data: [] })),
      ]);
      setTransactions(tRes.data);
      setAppointments(aRes.data || []);
      setUnits(uRes.data || []);
    } catch {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { load(); loadCash(); }, [load, loadCash]);
  useEffect(() => { loadSummary(filters); }, [filters, loadSummary]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiCall("post", "/transactions", {
        ...form,
        amount: parseFloat(form.amount),
        payment_method: form.payment_method || null,
        date: form.date ? new Date(form.date).toISOString() : null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        paid_at: form.paid_at ? new Date(form.paid_at).toISOString() : null,
        bank: form.bank || null,
        invoice_number: form.invoice_number || null,
        status: form.status || "pago",
      });
      toast.success("Transação registrada");
      setDialogOpen(false);
      setForm({ type: "receita", category: "", amount: "", payment_method: "dinheiro", description: "", date: "", due_date: "", paid_at: "", bank: "", invoice_number: "", status: "pago" });
      await load();
      await loadSummary(filters);
      await loadCash();
    } catch { toast.error("Erro ao salvar"); }
  };

  const handleDelete = async (id) => {
    if (!isMaster) {
      toast.error("Apenas master pode excluir. Para reverter uma venda, use Estornar em Comandas.");
      return;
    }
    if (!window.confirm("⚠ Exclusão está BLOQUEADA — use Estornar em Comandas. Continuar mesmo assim (erro 403)?")) return;
    try {
      await apiCall("delete", `/transactions/${id}`);
      toast.success("Excluída");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bloqueado"); }
  };

  const openCashDialog = async (mode) => {
    setCashMode(mode);
    setCashOpeningAmount("");
    setCashClosingDeclared(String(expectedCash.toFixed(2)));
    setCashNotes("");
    // iter67: reset movement fields
    setMvKind("withdrawal");
    setMvAmount("");
    setMvReason("");
    setCloseSummary(null);
    setCashDialogOpen(true);
    // Se está fechando, carrega o breakdown detalhado
    if (mode === "close" && cashSession) {
      try {
        const r = await apiCall("get", `/cash-sessions/${cashSession.session_id}/summary`);
        setCloseSummary(r.data);
      } catch { /* silencioso */ }
    }
  };

  // iter67: registra sangria/reforço
  const doCashMovement = async () => {
    if (!cashSession) return;
    if (!mvAmount || parseFloat(mvAmount) <= 0) { toast.error("Valor deve ser > 0"); return; }
    if (!(mvReason || "").trim()) { toast.error("Motivo é obrigatório"); return; }
    try {
      const r = await apiCall("post", `/cash-sessions/${cashSession.session_id}/movement`, {
        kind: mvKind,
        amount: parseFloat(mvAmount),
        reason: mvReason.trim(),
      });
      toast.success(`${mvKind === "withdrawal" ? "Sangria" : "Reforço"} de ${currency(r.data.amount)} registrado`);
      setCashDialogOpen(false);
      await loadCash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar movimento");
    }
  };

  const doOpenCash = async () => {
    try {
      const r = await apiCall("post", "/cash-sessions/open", {
        opening_amount: parseFloat(cashOpeningAmount || "0"),
        notes: cashNotes || "",
      });
      setCashSession(r.data);
      toast.success("Caixa aberto");
      setCashDialogOpen(false);
      await loadCash();
      await loadSummary(filters);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao abrir"); }
  };

  const doCloseCash = async () => {
    if (!cashSession) return;
    try {
      const r = await apiCall("post", `/cash-sessions/${cashSession.session_id}/close`, {
        closing_declared_amount: parseFloat(cashClosingDeclared || "0"),
        notes: cashNotes || "",
      });
      const diff = r.data.difference;
      if (Math.abs(diff) < 0.01) toast.success("Caixa fechado — bateu exatamente!");
      else if (diff > 0) toast.info(`Caixa fechado — sobra de ${currency(diff)}`);
      else toast.warning(`Caixa fechado — falta ${currency(Math.abs(diff))}`);
      // iter67: abre PDF de conferência em nova aba automaticamente
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const tok = localStorage.getItem("token") || localStorage.getItem("access_token");
        const url = `${backendUrl}/api/cash-sessions/${cashSession.session_id}/receipt.pdf`;
        // Baixa via fetch para injetar Authorization
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
        if (resp.ok) {
          const blob = await resp.blob();
          const objUrl = URL.createObjectURL(blob);
          window.open(objUrl, "_blank");
        }
      } catch { /* pdf best-effort */ }
      setCashDialogOpen(false);
      await loadCash();
      await loadSummary(filters);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao fechar"); }
  };

  // ==== Filtragem local da tabela usando os mesmos critérios do backend ====
  const filtered = useMemo(() => {
    return transactions.filter((t) => {
      const d = new Date(t.date);
      if (filters.from && d < new Date(`${filters.from}T00:00:00`)) return false;
      if (filters.to && d > new Date(`${filters.to}T23:59:59`)) return false;
      if (filters.type !== "all" && t.type !== filters.type) return false;
      if (filters.payment_method !== "all" && t.payment_method !== filters.payment_method) return false;
      if (filters.category !== "all" && t.category !== filters.category) return false;
      if (filters.unit_id !== "all" && t.unit_id !== filters.unit_id) return false;
      return true;
    });
  }, [transactions, filters]);

  const categories = useMemo(() => {
    const set = new Set(transactions.map(t => t.category).filter(Boolean));
    return Array.from(set).sort();
  }, [transactions]);

  const totals = summary?.totals || { revenue: 0, expense: 0, tips: 0, net: 0, cash_in: 0, cash_out: 0, expected_cash: 0, opening_amount: 0 };
  const breakdown = summary?.breakdown || { credit_deposited: 0, credit_consumed: 0, fiado_generated: 0, fiado_received: 0, employee_advances: 0, expenses_excluding_advances: 0, voids: 0 };
  const byPM = summary?.by_payment_method || {};

  // Dados para gráfico de rosca — apenas entradas (positivos)
  const pieData = useMemo(() => {
    return Object.entries(byPM)
      .filter(([k, v]) => !k.startsWith("out_") && v > 0)
      .map(([k, v]) => ({ name: PM_LABELS[k] || k, key: k, value: v }));
  }, [byPM]);

  // Filtros ativos indicator
  const filtersActive = filters.from !== todayISO() || filters.to !== todayISO() || filters.type !== "all" || filters.payment_method !== "all" || filters.category !== "all" || filters.unit_id !== "all";

  const setPMFilter = (pmKey) => {
    setFilters(f => ({ ...f, payment_method: f.payment_method === pmKey ? "all" : pmKey }));
  };

  return (
    <div className="space-y-5" data-testid="finance-page">
      <PageHeader overline="Financeiro" title="Movimentações e Caixa" subtitle="Fonte única: cards, gráficos e tabela usam o mesmo endpoint.">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { load(); loadCash(); loadSummary(filters); }} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="add-transaction-button">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Nova Transação
          </Button>
        </div>
      </PageHeader>

      {/* SESSÃO DE CAIXA */}
      <Card className="p-4 border-2 border-primary/20 bg-primary/5" data-testid="cash-session-panel">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-primary" strokeWidth={1.5} />
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Sessão de Caixa</div>
              {cashSession ? (
                <div>
                  <div className="text-sm">
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                      <Unlock className="h-3.5 w-3.5" strokeWidth={2} /> Caixa aberto
                    </span>
                    {" — abriu em "}<b>{dtFmt(cashSession.opened_at)}</b>{" com "}<b>{currency(cashSession.opening_amount)}</b>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Aberto por {cashSession.opened_by_name}
                  </div>
                </div>
              ) : (
                <div className="text-sm">
                  <span className="inline-flex items-center gap-1 text-slate-700">
                    <Lock className="h-3.5 w-3.5" strokeWidth={2} /> Nenhum caixa aberto
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {cashSession && (
              <div className="text-right">
                <div className="text-xs uppercase text-muted-foreground">Valor esperado no caixa</div>
                <div className="text-xl font-light" data-testid="expected-cash">{currency(expectedCash)}</div>
              </div>
            )}
            {cashSession ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openCashDialog("movement")}
                  data-testid="cash-movement-btn"
                  title="Registrar sangria (retirada) ou reforço"
                >
                  Sangria / Reforço
                </Button>
                <Button size="sm" onClick={() => openCashDialog("close")} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="close-cash-btn">
                  <Lock className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Fechar caixa
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => openCashDialog("open")} data-testid="open-cash-btn">
                <Unlock className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Abrir caixa
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* FILTROS */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-muted-foreground">
          <Filter className="h-4 w-4" strokeWidth={1.5} /> Filtros
          {filtersActive && (
            <Button variant="ghost" size="sm" className="ml-auto h-7 px-2 text-xs" onClick={() => setFilters(DEFAULT_FILTERS)} data-testid="clear-filters">
              <X className="h-3 w-3 mr-1" strokeWidth={1.5} /> Limpar
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="filter-from" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="filter-to" />
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="receita">Entradas</SelectItem>
                <SelectItem value="despesa">Saídas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Pagamento</Label>
            <Select value={filters.payment_method} onValueChange={(v) => setFilters({ ...filters, payment_method: v })}>
              <SelectTrigger data-testid="filter-payment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(PM_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {units.length > 1 && (
            <div>
              <Label className="text-xs">Unidade</Label>
              <Select value={filters.unit_id} onValueChange={(v) => setFilters({ ...filters, unit_id: v })}>
                <SelectTrigger data-testid="filter-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {units.map((u) => <SelectItem key={u.unit_id} value={u.unit_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </Card>

      {/* CARDS: TOTAIS PRINCIPAIS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard title="Total do período" value={currency(totals.revenue)} color="#16A34A" testid="card-total-revenue" />
        <MetricCard title="Saídas" value={currency(totals.expense)} color="#DC2626" testid="card-total-expense" />
        <MetricCard title="Líquido" value={currency(totals.net)} color="#0F172A" subtitle="Receitas − Saídas" testid="card-net" />
        <MetricCard title="Gorjetas (do profissional)" value={currency(totals.tips)} color="#7C3AED" testid="card-tips" />
      </div>

      {/* CARDS: POR FORMA DE PAGAMENTO (clicável — filtra a tabela) */}
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-medium">Por forma de pagamento (clique para filtrar)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {["dinheiro", "pix", "credito", "debito", "transferencia", "boleto", "credito_cliente"].map((pm) => (
            <PMCard
              key={pm}
              label={PM_LABELS[pm]}
              value={byPM[pm] || 0}
              color={PM_COLORS[pm]}
              active={filters.payment_method === pm}
              onClick={() => setPMFilter(pm)}
              testid={`pm-card-${pm}`}
            />
          ))}
        </div>
      </div>

      {/* BREAKDOWN: fiado / crédito / vale / estorno */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <BreakdownCard title="Fiado gerado" value={breakdown.fiado_generated} icon="⚠" color="#EF4444" tooltip="Passivo criado (invoice) — NÃO é caixa recebido" />
        <BreakdownCard title="Fiado recebido" value={breakdown.fiado_received} icon="↓" color="#059669" tooltip="Pagamentos de fiado no período — entra no caixa" />
        <BreakdownCard title="Crédito depositado" value={breakdown.credit_deposited} icon="↑" color="#EAB308" tooltip="Cliente colocou saldo — passivo, NÃO é receita" />
        <BreakdownCard title="Crédito consumido" value={breakdown.credit_consumed} icon="↔" color="#EC4899" tooltip="Cliente pagou com saldo — receita, mas não caixa novo" />
        <BreakdownCard title="Vales/salário" value={breakdown.employee_advances} icon="✂" color="#F97316" tooltip="Pagamentos a funcionários (integrados)" />
        <BreakdownCard title="Outras despesas" value={breakdown.expenses_excluding_advances} icon="−" color="#DC2626" tooltip="Despesas exceto vales/salários" />
        <BreakdownCard title="Estornos" value={breakdown.voids} icon="↺" color="#64748B" tooltip="Vendas estornadas no período (já subtraídas da receita)" />
      </div>

      {/* GRÁFICOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {pieData.length > 0 ? (
          <Card className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-medium">Distribuição por pagamento</div>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} label={(e) => `${e.name}: ${currency(e.value)}`}>
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={PM_COLORS[entry.key] || PM_COLORS.outros} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => currency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        ) : (
          <Card className="p-4 flex items-center justify-center h-[290px] text-xs text-muted-foreground">
            Sem dados de entrada no período
          </Card>
        )}

        {summary?.series_by_day?.length > 0 ? (
          <Card className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-medium">Entradas × Saídas por dia</div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={summary.series_by_day}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="day" fontSize={10} />
                <YAxis fontSize={10} tickFormatter={(v) => currency(v)} width={80} />
                <Tooltip formatter={(v) => currency(v)} />
                <Legend fontSize={10} />
                <Bar dataKey="revenue" name="Entradas" fill="#16A34A" />
                <Bar dataKey="expense" name="Saídas" fill="#DC2626" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        ) : (
          <Card className="p-4 flex items-center justify-center h-[290px] text-xs text-muted-foreground">
            Amplie o período (mais de 1 dia) para ver a série temporal
          </Card>
        )}
      </div>

      {/* TABELA */}
      <Card className="overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground">
          <span>Movimentações · <b>{filtered.length}</b> linha(s) · Total exibido: <b>{currency(filtered.reduce((s, t) => s + (t.type === "receita" ? t.amount : -t.amount), 0))}</b></span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs">Data / Hora</TableHead>
              <TableHead className="text-xs">Cliente</TableHead>
              <TableHead className="text-xs">Serviço</TableHead>
              <TableHead className="text-xs">Tipo</TableHead>
              <TableHead className="text-xs">Categoria</TableHead>
              <TableHead className="text-xs">Pagamento</TableHead>
              <TableHead className="text-xs">Descrição</TableHead>
              <TableHead className="text-xs">Registrado por</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">Valor</TableHead>
              <TableHead className="text-right text-xs">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center py-12 text-muted-foreground">Nenhuma transação encontrada.</TableCell></TableRow>
            ) : filtered.map((t) => {
              const appt = t.appointment_id ? apptMap[t.appointment_id] : null;
              const clientName = appt?.client_name || (t.description || "").split("—")[1]?.trim() || "—";
              const serviceName = appt?.service_names || "—";
              const isVoid = (t.category || "").toLowerCase().includes("estorno");
              return (
                <TableRow key={t.transaction_id} data-testid={`transaction-row-${t.transaction_id}`} className={isVoid ? "bg-slate-50" : ""}>
                  <TableCell className="text-xs whitespace-nowrap">{dtFmt(t.date)}</TableCell>
                  <TableCell className="text-sm font-medium max-w-[180px] truncate">{clientName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{serviceName}</TableCell>
                  <TableCell>
                    {t.type === "receita" ? (
                      <div className="flex items-center gap-1 text-xs" style={{ color: (t.category || "").toLowerCase() === "gorjeta" ? "#7C3AED" : "#166534" }}>
                        <ArrowDownCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {(t.category || "").toLowerCase() === "gorjeta" ? "Gorjeta" : "Entrada"}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-destructive">
                        <ArrowUpCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Saída
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {t.category}
                    {isVoid && <span className="ml-1 text-[9px] px-1 rounded bg-slate-200 text-slate-700">estorno</span>}
                  </TableCell>
                  <TableCell>
                    {t.payment_method ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium uppercase" style={{ background: `${PM_COLORS[t.payment_method] || "#94A3B8"}20`, color: PM_COLORS[t.payment_method] || "#64748B" }}>
                        {PM_LABELS[t.payment_method] || t.payment_method}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate" title={t.description}>{t.description || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.created_by_name || "—"}</TableCell>
                  <TableCell>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded font-medium" style={{ background: t.status === "pendente" ? "#FEF3C7" : "#D1FAE5", color: t.status === "pendente" ? "#92400E" : "#166534" }}>
                      {t.status || "pago"}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium text-sm" style={{ color: t.type === "receita" ? "#166534" : "#DC2626" }}>
                    {t.type === "receita" ? "+" : "−"} {currency(t.amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {isMaster && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(t.transaction_id)} title="Excluir (bloqueado — use Estornar em Comandas)" data-testid={`delete-transaction-${t.transaction_id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* DIALOG: Nova transação */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-light text-2xl">Nova Transação</DialogTitle><DialogDescription className="text-xs">Registro contábil imutável. Para reverter, use Estornar em Comandas.</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-3">
            <div><Label className="text-xs">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="receita">Receita</SelectItem><SelectItem value="despesa">Despesa</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Categoria</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex: Atendimento, Aluguel, Vale" required data-testid="transaction-category-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Valor (R$)</Label><Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="transaction-amount-input" /></div>
              <div><Label className="text-xs">Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="transaction-date-input" /></div>
            </div>
            <div><Label className="text-xs">Forma de Pagamento</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="transaction-payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PM_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Descrição</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="transaction-description-input" /></div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="transaction-save-button">Registrar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Abrir/Fechar caixa / Movimento (iter67) */}
      <Dialog open={cashDialogOpen} onOpenChange={setCashDialogOpen}>
        <DialogContent className={cashMode === "close" ? "sm:max-w-lg" : "sm:max-w-md"}>
          <DialogHeader>
            <DialogTitle className="font-light text-2xl">
              {cashMode === "open" ? "Abrir caixa"
                : cashMode === "close" ? "Fechar caixa"
                : "Sangria / Reforço"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {cashMode === "open"
                ? "Registre o valor em dinheiro que está no caixa neste momento."
                : cashMode === "close"
                ? "Confira o valor no caixa e informe abaixo. Um recibo PDF de conferência será gerado."
                : "Registre uma retirada (sangria) ou entrada extra (reforço) manualmente."}
            </DialogDescription>
          </DialogHeader>
          {cashMode === "open" ? (
            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-xs">Valor inicial em dinheiro (R$)</Label>
                <Input type="number" step="0.01" min="0" value={cashOpeningAmount} onChange={(e) => setCashOpeningAmount(e.target.value)} placeholder="0.00" data-testid="cash-opening-input" />
              </div>
              <div>
                <Label className="text-xs">Notas (opcional)</Label>
                <Input value={cashNotes} onChange={(e) => setCashNotes(e.target.value)} placeholder="Ex: troco do dia" data-testid="cash-notes-input" />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setCashDialogOpen(false)}>Cancelar</Button>
                <Button onClick={doOpenCash} data-testid="confirm-open-cash">Abrir caixa</Button>
              </DialogFooter>
            </div>
          ) : cashMode === "movement" ? (
            <div className="space-y-3 mt-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMvKind("withdrawal")}
                  className={`flex-1 h-10 rounded-md border text-sm ${mvKind === "withdrawal" ? "bg-rose-600 border-rose-600 text-white" : "bg-background"}`}
                  data-testid="mv-kind-withdrawal"
                >
                  ⬇ Sangria (retirada)
                </button>
                <button
                  type="button"
                  onClick={() => setMvKind("reinforcement")}
                  className={`flex-1 h-10 rounded-md border text-sm ${mvKind === "reinforcement" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-background"}`}
                  data-testid="mv-kind-reinforcement"
                >
                  ⬆ Reforço (entrada)
                </button>
              </div>
              <div>
                <Label className="text-xs">Valor (R$) *</Label>
                <Input type="number" step="0.01" min="0.01" value={mvAmount} onChange={(e) => setMvAmount(e.target.value)} placeholder="0.00" data-testid="mv-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Motivo * <span className="text-muted-foreground">(obrigatório para auditoria)</span></Label>
                <Input value={mvReason} onChange={(e) => setMvReason(e.target.value)} placeholder="Ex: Pagamento fornecedor, compra material, troco extra..." data-testid="mv-reason-input" />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setCashDialogOpen(false)}>Cancelar</Button>
                <Button onClick={doCashMovement} data-testid="confirm-mv-btn">Registrar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 mt-3">
              {/* iter67: breakdown detalhado por forma de pagamento */}
              {closeSummary && (
                <div className="border rounded-md p-3 bg-slate-50 space-y-1.5" data-testid="close-breakdown">
                  <div className="text-[10px] uppercase text-muted-foreground font-semibold">Resumo por forma de pagamento</div>
                  {(closeSummary.payment_breakdown || []).length === 0 && (
                    <div className="text-xs text-muted-foreground">Sem movimentos ainda</div>
                  )}
                  {(closeSummary.payment_breakdown || []).map((p) => (
                    <div key={p.payment_method} className="flex justify-between text-xs">
                      <span className="capitalize">{p.payment_method}</span>
                      <span>
                        {p.count} × <span className="font-medium">{currency(p.in)}</span>
                        {p.out > 0 && <span className="text-rose-700"> −{currency(p.out)}</span>}
                      </span>
                    </div>
                  ))}
                  {(closeSummary.movements || []).length > 0 && (
                    <>
                      <div className="text-[10px] uppercase text-muted-foreground font-semibold pt-2 border-t">Movimentos manuais</div>
                      {closeSummary.movements.map((m) => (
                        <div key={m.movement_id} className="flex justify-between text-xs">
                          <span>{m.kind === "withdrawal" ? "⬇ Sangria" : "⬆ Reforço"} — {m.reason}</span>
                          <span className={m.kind === "withdrawal" ? "text-rose-700" : "text-emerald-700"}>
                            {m.kind === "withdrawal" ? "−" : "+"}{currency(m.amount)}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-3 rounded border bg-slate-50">
                  <div className="text-[10px] uppercase text-muted-foreground">Esperado</div>
                  <div className="text-lg font-medium">{currency(expectedCash)}</div>
                </div>
                <div className="p-3 rounded border">
                  <div className="text-[10px] uppercase text-muted-foreground">Abertura</div>
                  <div className="text-lg font-light">{currency(cashSession?.opening_amount)}</div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Valor conferido no caixa (R$)</Label>
                <Input type="number" step="0.01" min="0" value={cashClosingDeclared} onChange={(e) => setCashClosingDeclared(e.target.value)} data-testid="cash-closing-input" />
              </div>
              <div>
                <Label className="text-xs">Notas do fechamento (opcional)</Label>
                <Input value={cashNotes} onChange={(e) => setCashNotes(e.target.value)} data-testid="cash-closing-notes" />
              </div>
              {parseFloat(cashClosingDeclared || "0") !== expectedCash && cashClosingDeclared !== "" && (
                <div className="text-xs p-2 rounded bg-amber-50 border border-amber-200 text-amber-900">
                  {parseFloat(cashClosingDeclared) > expectedCash
                    ? `⬆ Sobra de ${currency(parseFloat(cashClosingDeclared) - expectedCash)}`
                    : `⬇ Falta ${currency(expectedCash - parseFloat(cashClosingDeclared))}`}
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setCashDialogOpen(false)}>Cancelar</Button>
                <Button onClick={doCloseCash} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-close-cash">Fechar caixa + PDF</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ title, value, color, subtitle, testid }) {
  return (
    <Card className="p-4 shadow-soft" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">{title}</div>
      <div className="text-2xl font-light" style={{ color }}>{value}</div>
      {subtitle && <div className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</div>}
    </Card>
  );
}

function PMCard({ label, value, color, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-3 rounded border transition-all ${active ? "border-2 shadow-md" : "border hover:shadow-sm"}`}
      style={{ borderColor: active ? color : undefined, background: active ? `${color}10` : undefined }}
    >
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-light" style={{ color }}>{currency(value)}</div>
      {active && <div className="text-[9px] text-muted-foreground mt-0.5">filtrando…</div>}
    </button>
  );
}

function BreakdownCard({ title, value, icon, color, tooltip }) {
  return (
    <Card className="p-3" title={tooltip}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <span style={{ color }}>{icon}</span> {title}
      </div>
      <div className="text-base font-medium mt-0.5" style={{ color: value > 0 ? color : "#64748B" }}>
        {currency(value)}
      </div>
    </Card>
  );
}
